import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    FormControlLabel,
    IconButton,
    ListSubheader,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import to from 'await-to-js';
import ApiClient from 'c-data/apiClient';
import { useAPIClientRequest, useDateUtils } from 'c-hooks';
import { useUserPermissions } from 'c-auth-module/Hooks';
import type { Displays_AudienceSource } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import type { AudienceVersionOption } from './utils';
import {
    audienceSourceFilterOptions,
    audienceSourceLabel,
    audienceVersionReleaseDays,
    audienceVersionRole,
    buildAudienceVersionOptions,
    selectedAudienceVersionOptions,
    visibleAudienceVersionOptions,
    withoutInactiveSourceIds,
} from './utils';
import type { AudienceVersionId } from './popSettingsPayload';
import { appendVersionId, moveVersionId, removeVersionId } from './popSettingsPayload';

/** The API has no maximum page size; this is comfortably more than every version (about 56). */
const LOAD_ALL = 10000;
const SOURCE_READ = [PermissionName.DisplaysAudience_sourceRead];

type Props = {
    /** The chosen ids in priority order: index 0 is the primary audience. */
    value: AudienceVersionId[];
    onChange: (ids: AudienceVersionId[]) => void;
};

const SourceNames: React.FC<{ source: Displays_AudienceSource }> = ({ source }) => (
    <Box>
        <Typography variant="body2">{audienceSourceLabel(source)}</Typography>
        <Typography variant="caption" color="text.secondary" component="div">
            {source.name}
        </Typography>
    </Box>
);

/**
 * UL-8034 -- the Pop Settings audience version picker. See this folder's AGENTS.md.
 *
 * MUI Autocomplete is used directly because `value` (the form's ordered id list) is the only source
 * of order: a pick appends, a removal closes the gap, and only the user's move reorders. The shared
 * Autocomplete wrapper returns the selection in option order instead, which would swap the primary.
 */
const AudienceVersionPicker: React.FC<Props> = ({ value, onChange }) => {
    const t = useCommonTranslation();
    const { formatDateString } = useDateUtils();
    const { hasAll } = useUserPermissions();
    // Including the source throws for anyone without this permission, and the schedule page's
    // own gate does not imply it, so ask for source data only when it is held.
    const canReadSources = hasAll(SOURCE_READ);

    const {
        start: loadVersions,
        data: versionsResponse,
        isLoading: versionsLoading,
        hasFailed: versionsFailed,
    } = useAPIClientRequest(ApiClient.Entities.Displays_AudienceVersion.list);
    const {
        start: loadSources,
        data: sourcesResponse,
        isLoading: sourcesLoading,
    } = useAPIClientRequest(ApiClient.Entities.Displays_AudienceSource.list);

    useEffect(() => {
        to(loadVersions({ perPage: LOAD_ALL, includes: canReadSources ? ['source'] : [] }));
        if (canReadSources) to(loadSources({ perPage: LOAD_ALL }));
    }, [canReadSources, loadSources, loadVersions]);

    const sources = useMemo<Displays_AudienceSource[] | null>(
        () => (canReadSources ? sourcesResponse?.data?.data ?? [] : null),
        [canReadSources, sourcesResponse],
    );
    const options = useMemo(
        () => buildAudienceVersionOptions(versionsResponse?.data?.data ?? [], sources),
        [versionsResponse, sources],
    );
    const selected = useMemo(
        () => selectedAudienceVersionOptions(value, options),
        [value, options],
    );
    // Placeholders for unavailable ids are options too, or MUI warns that the value has no match.
    const allOptions = useMemo(
        () => [...options, ...selected.filter(option => !option.version)],
        [options, selected],
    );

    const [showInactive, setShowInactive] = useState(false);
    const [sourceIds, setSourceIds] = useState<number[]>([]);
    const sourceOptions = useMemo(
        () => audienceSourceFilterOptions(sources ?? [], showInactive),
        [sources, showInactive],
    );
    const sourcesById = useMemo(
        () => new Map(options.flatMap(o => (o.source ? [[o.source.id, o.source]] : []))),
        [options],
    );

    const toggleInactive = useCallback(
        (_: unknown, checked: boolean) => {
            setShowInactive(checked);
            if (!checked) setSourceIds(ids => withoutInactiveSourceIds(ids, sources ?? []));
        },
        [sources],
    );

    const filterOptions = useCallback(
        (opts: AudienceVersionOption[], { inputValue }: { inputValue: string }) =>
            visibleAudienceVersionOptions(opts, {
                selectedIds: value,
                showInactive,
                sourceIds,
                search: inputValue,
            }),
        [value, showInactive, sourceIds],
    );

    const versionLabel = useCallback(
        (option: AudienceVersionOption) =>
            option.version?.version_name ??
            t('Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceVersionUnavailable', {
                id: option.id,
            }),
        [t],
    );

    const releaseDates = useCallback(
        (option: AudienceVersionOption) => {
            const { firstRelease, lastRevision } = audienceVersionReleaseDays(option.version);
            const label = (
                path:
                    | 'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceVersionFirstRelease'
                    | 'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceVersionLastRevision',
                day: string | null,
            ) => {
                // A date-only string parses as local midnight, so the day shown is the day sent.
                const date = day ? formatDateString({ date: day }) : '';
                return date ? t(path, { date }) : null;
            };
            return [
                label(
                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceVersionFirstRelease',
                    firstRelease,
                ),
                label(
                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceVersionLastRevision',
                    lastRevision,
                ),
            ]
                .filter(Boolean)
                .join(' · ');
        },
        [formatDateString, t],
    );

    const dragIndexRef = useRef<number | null>(null);
    const move = (from: number, toIndex: number) => {
        if (toIndex < 0 || toIndex >= value.length || from === toIndex) return;
        onChange(moveVersionId(value, from, toIndex));
    };

    return (
        <Stack gap={1.5}>
            {canReadSources && (
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    gap={2}
                    alignItems={{ md: 'center' }}
                >
                    <Autocomplete<Displays_AudienceSource, true>
                        multiple
                        size="small"
                        sx={{ flex: 1 }}
                        loading={sourcesLoading}
                        options={sourceOptions}
                        value={sourceOptions.filter(source => sourceIds.includes(source.id))}
                        onChange={(_, next) => setSourceIds(next.map(source => source.id))}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        getOptionLabel={audienceSourceLabel}
                        filterSelectedOptions
                        renderOption={(props, source) => {
                            const { key, ...optionProps } = props as typeof props & {
                                key: React.Key;
                            };
                            return (
                                <li key={key} {...optionProps}>
                                    <SourceNames source={source} />
                                </li>
                            );
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label={t(
                                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceSourceFilterLabel',
                                )}
                                placeholder={
                                    sourceIds.length
                                        ? undefined
                                        : t(
                                              'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceSourceFilterPlaceholder',
                                          )
                                }
                            />
                        )}
                    />
                    <FormControlLabel
                        control={<Switch checked={showInactive} onChange={toggleInactive} />}
                        label={t(
                            'Modules.Main.Campaigns.CampaignSchedule.popSettings.showInactiveSources',
                        )}
                    />
                </Stack>
            )}
            <Autocomplete<AudienceVersionOption, true, true>
                multiple
                disableClearable
                disableCloseOnSelect
                size="small"
                loading={versionsLoading}
                options={allOptions}
                value={selected}
                onChange={(_, __, reason, details) => {
                    if (!details) return;
                    if (reason === 'selectOption') {
                        onChange(appendVersionId(value, details.option.id));
                    } else if (reason === 'removeOption') {
                        onChange(removeVersionId(value, details.option.id));
                    }
                }}
                filterOptions={filterOptions}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                getOptionLabel={versionLabel}
                groupBy={canReadSources ? option => String(option.source?.id ?? '') : undefined}
                renderGroup={params => {
                    const source = sourcesById.get(Number(params.group));
                    return (
                        <li key={params.key}>
                            <ListSubheader component="div" sx={{ lineHeight: 1.4, py: 1 }}>
                                {source ? (
                                    <SourceNames source={source} />
                                ) : (
                                    t(
                                        'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceSourceUnknown',
                                    )
                                )}
                            </ListSubheader>
                            <Box component="ul" sx={{ p: 0 }}>
                                {params.children}
                            </Box>
                        </li>
                    );
                }}
                renderOption={(props, option) => {
                    const { key, ...optionProps } = props as typeof props & { key: React.Key };
                    const dates = releaseDates(option);
                    return (
                        <li key={key} {...optionProps}>
                            <Box>
                                <Typography variant="body2">{versionLabel(option)}</Typography>
                                {dates && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        component="div"
                                    >
                                        {dates}
                                    </Typography>
                                )}
                            </Box>
                        </li>
                    );
                }}
                renderTags={() => null}
                renderInput={params => (
                    <TextField
                        {...params}
                        label={t(
                            'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceVersionsLabel',
                        )}
                    />
                )}
            />
            {versionsFailed && (
                <Alert severity="error" variant="outlined">
                    {t(
                        'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceVersionsLoadError',
                    )}
                </Alert>
            )}
            <Typography variant="caption" color="text.secondary">
                {t('Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceVersionsHelp')}
            </Typography>
            {selected.length > 0 && (
                <Stack
                    component="ol"
                    gap={0.5}
                    sx={{ listStyle: 'none', p: 0, m: 0 }}
                    aria-label={t(
                        'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceVersionsLabel',
                    )}
                >
                    {selected.map((option, index) => (
                        <Stack
                            component="li"
                            key={option.id}
                            direction="row"
                            alignItems="center"
                            gap={1}
                            draggable
                            onDragStart={() => {
                                dragIndexRef.current = index;
                            }}
                            onDragOver={(e: React.DragEvent) => e.preventDefault()}
                            onDrop={() => {
                                const from = dragIndexRef.current;
                                dragIndexRef.current = null;
                                if (from != null) move(from, index);
                            }}
                            sx={{
                                px: 1,
                                py: 0.5,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                cursor: 'grab',
                            }}
                        >
                            <DragIndicatorIcon fontSize="small" color="action" />
                            <Typography variant="body2" sx={{ minWidth: '1.5em' }}>
                                {index + 1}
                            </Typography>
                            <Typography
                                variant="body2"
                                color={index === 0 ? 'primary' : 'text.secondary'}
                                fontWeight={index === 0 ? 'bold' : undefined}
                                sx={{ minWidth: '5em' }}
                            >
                                {t(
                                    audienceVersionRole(index) === 'primary'
                                        ? 'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceVersionPrimary'
                                        : 'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceVersionFallback',
                                )}
                            </Typography>
                            <Box flex={1} minWidth={0}>
                                <Typography variant="body2">
                                    {option.source
                                        ? `${audienceSourceLabel(option.source)} · ${versionLabel(
                                              option,
                                          )}`
                                        : versionLabel(option)}
                                </Typography>
                                {option.source && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        component="div"
                                    >
                                        {option.source.name}
                                    </Typography>
                                )}
                            </Box>
                            <IconButton
                                size="small"
                                disabled={index === 0}
                                onClick={() => move(index, index - 1)}
                                aria-label={t(
                                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceVersionMoveUp',
                                )}
                            >
                                <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                disabled={index === selected.length - 1}
                                onClick={() => move(index, index + 1)}
                                aria-label={t(
                                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceVersionMoveDown',
                                )}
                            >
                                <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => onChange(removeVersionId(value, option.id))}
                                aria-label={t(
                                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.audienceVersionRemove',
                                )}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

export default AudienceVersionPicker;
